/**
 * Charts — hand-rolled SVG marks for the dashboard.
 *
 * The app ships no charting dependency; `DonutChart` established the pattern of
 * drawing marks directly and this follows it rather than pulling in a library
 * for four figures. Each chart is responsive by construction: the SVG carries a
 * `viewBox` and no fixed pixel width, so it scales with its grid cell.
 *
 * Accessibility is not optional here — every chart takes a `role="img"` with an
 * `aria-label` that enumerates the data, and every value is also present as
 * text (axis label, inline count, or legend), so nothing is conveyed by colour
 * or geometry alone.
 */

import { ReactNode, useId } from 'react';
import { Link } from 'react-router-dom';

/* ── shared card chrome ─────────────────────────────────────────────────── */

export function ChartCard({
  title,
  caption,
  action,
  children,
  span = 1,
}: {
  title: string;
  caption?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  span?: number;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      style={{
        gridColumn: `span ${span}`,
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-2xl)',
        boxShadow: 'var(--sh-sm)',
        padding: 'var(--sp-5)',
        minWidth: 0,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 id={id} style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
            {title}
          </h2>
          {caption && (
            <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', lineHeight: 'var(--lh-normal)' }}>
              {caption}
            </p>
          )}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/* ── histogram ──────────────────────────────────────────────────────────── */

export interface HistogramBin {
  lo: number;
  hi: number;
  count: number;
}

/**
 * Distribution columns. `markerAt` draws a labelled vertical rule (used for the
 * median) so the central tendency is readable without a separate stat.
 */
export function Histogram({
  bins,
  markerAt,
  markerLabel,
  unit = '%',
  ariaLabel,
}: {
  bins: HistogramBin[];
  markerAt?: number | null;
  markerLabel?: string;
  unit?: string;
  ariaLabel: string;
}) {
  const W = 620, H = 200, PAD_L = 34, PAD_B = 26, PAD_T = 12;
  const plot = bins.filter((b) => b.count > 0);
  const maxCount = Math.max(1, ...bins.map((b) => b.count));
  const domainHi = plot.length ? Math.max(...plot.map((b) => b.hi)) : 100;
  const visible = bins.filter((b) => b.lo < domainHi);
  const bw = (W - PAD_L - 8) / Math.max(1, visible.length);

  const y = (c: number) => PAD_T + (H - PAD_T - PAD_B) * (1 - c / maxCount);

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={ariaLabel} style={{ display: 'block', minWidth: 320 }}>
        {/* y gridlines */}
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line
              x1={PAD_L} x2={W - 8} y1={y(maxCount * f)} y2={y(maxCount * f)}
              stroke="var(--color-border)" strokeWidth="1" strokeDasharray={f === 0 ? undefined : '3 3'}
            />
            <text x={PAD_L - 6} y={y(maxCount * f) + 4} textAnchor="end" fontSize="10" fill="var(--color-ink-3)">
              {Math.round(maxCount * f)}
            </text>
          </g>
        ))}

        {visible.map((b, i) => {
          const h = Math.max(b.count > 0 ? 2 : 0, (H - PAD_T - PAD_B) * (b.count / maxCount));
          return (
            <g key={b.lo}>
              <rect
                x={PAD_L + i * bw + 2}
                y={H - PAD_B - h}
                width={Math.max(1, bw - 4)}
                height={h}
                rx="2"
                className="ig-hist-bar"
                fill="var(--brand-primary)"
                opacity={0.86}
              >
                <title>{`${b.lo}–${b.hi}${unit}: ${b.count}`}</title>
              </rect>
              {b.count > 0 && (
                <text
                  x={PAD_L + i * bw + bw / 2} y={H - PAD_B - h - 4}
                  textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--color-ink-2)"
                >
                  {b.count}
                </text>
              )}
              {i % 2 === 0 && (
                <text
                  x={PAD_L + i * bw + bw / 2} y={H - PAD_B + 14}
                  textAnchor="middle" fontSize="10" fill="var(--color-ink-3)"
                >
                  {b.lo}
                </text>
              )}
            </g>
          );
        })}

        {markerAt != null && markerAt <= domainHi && (
          <g>
            <line
              x1={PAD_L + (markerAt / domainHi) * (W - PAD_L - 8)}
              x2={PAD_L + (markerAt / domainHi) * (W - PAD_L - 8)}
              y1={PAD_T - 4} y2={H - PAD_B}
              stroke="var(--brand-accent)" strokeWidth="2" strokeDasharray="4 3"
            />
            <text
              x={PAD_L + (markerAt / domainHi) * (W - PAD_L - 8) + 6}
              y={PAD_T + 6} fontSize="10" fontWeight="700" fill="var(--brand-accent)"
            >
              {markerLabel}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

/* ── horizontal bars ────────────────────────────────────────────────────── */

export interface BarDatum {
  label: string;
  value: number;
  /** Optional second measure drawn as a lighter overlay (e.g. supply vs demand). */
  secondary?: number;
  emphasis?: boolean;
  /**
   * Where this bar leads. When set the row renders as a real <Link> — keyboard
   * focusable, middle-clickable, and showing its target in the status bar —
   * rather than a div with an onClick, which looks identical and is none of
   * those things.
   */
  href?: string;
}

export function HBars({
  data,
  valueLabel,
  secondaryLabel,
  ariaLabel,
  maxRows = 12,
}: {
  data: BarDatum[];
  valueLabel: string;
  secondaryLabel?: string;
  ariaLabel: string;
  maxRows?: number;
}) {
  const rows = data.slice(0, maxRows);
  const max = Math.max(1, ...rows.map((r) => Math.max(r.value, r.secondary ?? 0)));

  return (
    <div role="img" aria-label={ariaLabel} style={{ display: 'grid', gap: 'var(--sp-2-5)' }}>
      {rows.map((r) => {
        const rowStyle = {
          display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto',
          gap: 'var(--sp-3)', alignItems: 'center',
        } as const;
        const body = (
          <>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', marginBottom: 3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                fontWeight: r.emphasis ? 'var(--fw-semibold)' : 'var(--fw-normal)',
              }}
              title={r.label}
            >
              {r.emphasis && (
                <span
                  aria-label="critical"
                  style={{
                    display: 'inline-block', width: 6, height: 6, borderRadius: 999,
                    background: 'var(--color-status-red)', marginRight: 6, verticalAlign: 'middle',
                  }}
                />
              )}
              {r.label}
            </div>
            <div style={{ position: 'relative', height: 8, background: 'var(--color-bg)', borderRadius: 999, overflow: 'hidden' }}>
              <div
                className="ig-bar-fill"
                style={{
                  position: 'absolute', inset: 0, width: `${(r.value / max) * 100}%`,
                  background: r.emphasis ? 'var(--color-status-red)' : 'var(--brand-primary)',
                  borderRadius: 999,
                }}
              />
              {r.secondary != null && r.secondary > 0 && (
                <div
                  style={{
                    position: 'absolute', top: 0, bottom: 0, width: `${(r.secondary / max) * 100}%`,
                    background: 'var(--brand-accent)', borderRadius: 999, opacity: 0.9,
                  }}
                />
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 'var(--fs-sm)', fontVariantNumeric: 'tabular-nums', color: 'var(--color-ink-2)', fontWeight: 'var(--fw-semibold)' }}>
            {r.value.toLocaleString()}
            {r.secondary != null && (
              <span style={{ color: r.secondary === 0 ? 'var(--color-status-red)' : 'var(--color-ink-3)', fontWeight: 'var(--fw-normal)' }}>
                {' / '}{r.secondary}
              </span>
            )}
          </div>
          </>
        );
        // A real <Link> when it leads somewhere, a plain div otherwise: an anchor
        // is keyboard-focusable, middle-clickable and previews its target, none
        // of which a div-with-onClick gives you.
        return r.href ? (
          <Link key={r.label} to={r.href} className="ig-bar-row" style={{ ...rowStyle, textDecoration: 'none', color: 'inherit' }}>
            {body}
          </Link>
        ) : (
          <div key={r.label} style={rowStyle}>{body}</div>
        );
      })}
      <p style={{ margin: '4px 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
        {valueLabel}
        {secondaryLabel && <> · <span style={{ color: 'var(--brand-accent)' }}>▬</span> {secondaryLabel}</>}
      </p>
    </div>
  );
}

/* ── dual-series area over time ─────────────────────────────────────────── */

export interface SeriesPoint {
  label: string;
  a: number;
  b: number;
}

export function DualArea({
  points,
  aLabel,
  bLabel,
  ariaLabel,
}: {
  points: SeriesPoint[];
  aLabel: string;
  bLabel: string;
  ariaLabel: string;
}) {
  const W = 620, H = 190, PAD_L = 34, PAD_B = 28, PAD_T = 12;
  const max = Math.max(1, ...points.flatMap((p) => [p.a, p.b]));
  const n = Math.max(1, points.length - 1);
  const x = (i: number) => PAD_L + (i / n) * (W - PAD_L - 10);
  const y = (v: number) => PAD_T + (H - PAD_T - PAD_B) * (1 - v / max);

  const path = (key: 'a' | 'b') => points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p[key])}`).join(' ');
  const area = (key: 'a' | 'b') =>
    `${path(key)} L${x(points.length - 1)},${H - PAD_B} L${x(0)},${H - PAD_B} Z`;

  if (!points.length) return <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>No dated records.</p>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={ariaLabel} style={{ display: 'block', minWidth: 320 }}>
        <defs>
          <linearGradient id="ig-a" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity="0.26" />
            <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={PAD_L} x2={W - 10} y1={y(max * f)} y2={y(max * f)} stroke="var(--color-border)" strokeDasharray={f === 0 ? undefined : '3 3'} />
            <text x={PAD_L - 6} y={y(max * f) + 4} textAnchor="end" fontSize="10" fill="var(--color-ink-3)">{Math.round(max * f)}</text>
          </g>
        ))}

        <path d={area('a')} fill="url(#ig-a)" />
        <path d={path('a')} fill="none" stroke="var(--brand-primary)" strokeWidth="2" strokeLinejoin="round" />
        <path d={path('b')} fill="none" stroke="var(--brand-accent)" strokeWidth="2" strokeDasharray="5 3" strokeLinejoin="round" />

        {points.map((p, i) => (
          <g key={p.label}>
            <circle cx={x(i)} cy={y(p.a)} r="2.5" fill="var(--brand-primary)">
              <title>{`${p.label} · ${aLabel} ${p.a}`}</title>
            </circle>
            <circle cx={x(i)} cy={y(p.b)} r="2.5" fill="var(--brand-accent)">
              <title>{`${p.label} · ${bLabel} ${p.b}`}</title>
            </circle>
            {(i === 0 || i === points.length - 1 || i % 3 === 0) && (
              <text x={x(i)} y={H - PAD_B + 15} textAnchor="middle" fontSize="9" fill="var(--color-ink-3)">
                {p.label}
              </text>
            )}
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 'var(--sp-4)', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-2)', marginTop: 4 }}>
        <span><span style={{ color: 'var(--brand-primary)' }}>●</span> {aLabel}</span>
        <span><span style={{ color: 'var(--brand-accent)' }}>▬</span> {bLabel}</span>
      </div>
    </div>
  );
}
